// What must never reach Datadog: session and email-link tokens, Stripe secrets, API keys and
// email addresses. They travel in query strings (`#/auto-login?email=&token=`, SSE and verify
// calls with `?vektor-token=`, Stripe's `payment_intent_client_secret`), in paths
// (`#/auth/update-password/<token>`, `oauth/onboarding/<email>/checkAccount`) and in error text.
const SECRET_PARAMS = [
  'token',
  'vektor-token',
  'email',
  'apikey',
  'key',
  'payment_intent',
  'payment_intent_client_secret',
  'setup_intent',
  'setup_intent_client_secret',
];
const SECRET_PARAM_RE = new RegExp(`([?&#;](?:${SECRET_PARAMS.join('|')})=)[^&#\\s"']*`, 'gi');
const EMAIL_RE = /[^\s/?#&=:;,"'<>()[\]]+(?:@|%40)[^\s/?#&=:;,"'<>()[\]]+\.[a-z]{2,}/gi;
const SECRET_PATH_RE = /(\/(?:auth\/update-password|password\/reset)\/)[^/?#\s"']+/gi;
const ABSOLUTE_URL_RE = /https?:\/\/[^\s"'<>]+/gi;
// A path segment with two or more digits (ids, dates, plates, UUIDs, tokens), or a single digit.
// Static API segments never have more than one (`v3`, `...WorkgroupV2`).
const ID_SEGMENT_RE = /\d.*\d|^\d$/;

function redact(text) {
  return text
    .replace(SECRET_PARAM_RE, '$1[redacted]')
    .replace(SECRET_PATH_RE, '$1[redacted]')
    .replace(EMAIL_RE, '[email]');
}

function withoutQuery(part) {
  const queryStart = part.indexOf('?');
  return queryStart === -1 ? part : part.slice(0, queryStart);
}

/**
 * Drops the query string, both before the hash and inside it (hash routing keeps the route query
 * there), and redacts secrets left in the path. Usage analytics needs neither, and query strings
 * also carry search terms and filter values typed by users.
 * @param {string} url
 * @returns {string}
 */
export function scrubUrl(url) {
  if (typeof url !== 'string') return url;
  const hashStart = url.indexOf('#');
  const beforeHash = hashStart === -1 ? url : url.slice(0, hashStart);
  const hash = hashStart === -1 ? '' : url.slice(hashStart);
  return redact(withoutQuery(beforeHash) + withoutQuery(hash));
}

/**
 * Free text (error messages, stacks): URLs inside it are scrubbed like `scrubUrl`, then any
 * remaining secret parameter or email is redacted.
 * @param {string} text
 * @returns {string}
 */
export function scrubText(text) {
  if (typeof text !== 'string') return text;
  return redact(text.replace(ABSOLUTE_URL_RE, scrubUrl));
}

/**
 * Path of an axios request with ids replaced by `:id`, so calls to the same endpoint group
 * together: `/commute/rest/v3/routes/:id/activate`.
 * @param {{ baseURL?: string, url?: string }} config axios request config
 * @returns {string}
 */
export function apiPath({ baseURL = '', url = '' } = {}) {
  const full = /^https?:\/\//i.test(url) ? url : `${baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;
  const path = withoutQuery(full.replace(/^https?:\/\/[^/]+/i, '').split('#')[0]);
  return redact(path)
    .split('/')
    .map((segment) => (ID_SEGMENT_RE.test(segment) ? ':id' : segment))
    .join('/');
}
